import { ChangeEvent, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { WelcomeSection } from "components/general/WelcomeSection"
import { DropdownMenu } from "components/inputs/DropdownMenu"
import { ModalBonus } from "components/modals/ModalBonus"
import { TopBar } from "components/navigation/TopBar"
import { SearchInput } from "components/search/SearchInput"
import { Table } from "components/tables/Table"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { tenantOptions } from "constants/bonus"
import { tableBonusesColumns, tableFSBonusesColumns } from "constants/data"
import { PERMISSIONS } from "constants/permissions"

import { BonusService } from "services/bonus/bonus.service"

import { BonusType } from "types/api"

export const Bonueses = () => {
  const { user } = useProps()
  const { has } = usePermissions()
  const { t } = useTranslation(["translation"], { useSuspense: false })

  const [editRow, setEditRow] = useState(null)
  const [editRowDate, setEditRowDate] = useState(null)
  const [query, setQuery] = useState<string>("")
  const [bonusTenant, setBonusTenant] = useState<string>("")
  const [open, setOpen] = useState(false)
  const [tableUpdatedAt, setTableUpdatedAt] = useState<Date | number>(Date.now())
  const [currentTab, setCurrentTab] = useState<BonusType>(BonusType.DEPOSIT)

  const closeHandler = (): void => {
    setOpen(false)
    setEditRow(null)
    setTableUpdatedAt(Date.now())
  }

  const openHandler = (row: any) => {
    setEditRow(row)
    setOpen(true)
  }

  const handleCreate = () => {
    setEditRow(null)
    setOpen(true)
  }
  const actions = [
    {
      name: t("common.edit"),
      handler: (row: any) => openHandler(row)
    }
  ]

  const handleDateChange = async (rowId: string, dates: string[]) => {
    try {
      const formattedDates = dates.join(",")
      await BonusService.editBonus(rowId, { schedule: formattedDates })
      toast.success("Schedule updated successfully!")
    } catch (error) {
      toast.error("Failed to update schedule!")
    }
  }

  return (
    <div className="grid w-full gap-8 p-8 pt-0 grid-col-1">
      <TopBar title={t("pages.bonuses.title")} />
      {user && <WelcomeSection />}
      <div className="tabs gap-2">
        {Object.keys(BonusType)
          .filter((item) => item !== BonusType.CUSTOM && item !== BonusType.PROMO)
          .map((tab, index) => (
            <a
              key={index}
              onClick={() => setCurrentTab(tab as BonusType)}
              className={clsx(
                "flex cursor-pointer items-center text-left py-3 rounded-lg px-4 text-base border text-[rgba(6,7,21,1)] dark:text-white border-[rgba(132,_0,_168,_1)] dark:border-transparent transition-all dark:bg-[#0C0C1A] hover:border-[rgba(255,255,255,0.1)]",
                {
                  "transition-all text-white bg-[rgba(132,_0,_168,_1)] dark:bg-[rgba(132,_0,_168,_1)]": currentTab === tab,
                  "hover:border-[rgba(132,_0,_168,_0.8)] hover:dark:border-[rgba(132,_0,_168,_0.8)]  hover:bg-[rgba(169,78,206,0.2)] dark:hover:bg-[rgba(169,78,206,0.15)]":
                    currentTab !== tab
                }
              )}
            >
              {tab}
            </a>
          ))}
      </div>
      <div className="flex items-end">
        <div className="flex flex-1 space-x-4 max-w-md">
          <SearchInput placeholder={t("form.searchByTitle")} onChange={setQuery} />
          <DropdownMenu
            placeholder={t("form.allTenants")}
            options={tenantOptions}
            value={bonusTenant}
            onChange={(ev: ChangeEvent<HTMLSelectElement>) => setBonusTenant(ev.target.value)}
            rounded
          />
        </div>
        {user && has(PERMISSIONS.BONUS_CREATE) ? (
          <button
            className="ml-auto btn btn-primary bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent dark:text-white hover:text-white"
            onClick={handleCreate}
          >
            {t("common.create")}
          </button>
        ) : null}
      </div>

      <Table
        title={t("bonuses.tableBonuses")}
        handler={(page: number, pageSize: number, _id: number | undefined, filters: any, sortRow?: string, sortOrder?: string) =>
          BonusService.getBonusesList({ page, pageSize, filters, sortRow, sortOrder })
        }
        columns={currentTab === BonusType.FREESPINS ? tableFSBonusesColumns : tableBonusesColumns}
        filters={{ q: query, tenant: bonusTenant, type: currentTab }}
        onRowClick={(row: any) => openHandler(row)}
        actions={actions}
        updatedAt={tableUpdatedAt}
        onDatePickerClick={(row: any) => setEditRowDate(row)}
        dateData={editRowDate}
        handleDateChange={handleDateChange}
      />

      <ModalBonus data={editRow} handler={closeHandler} open={open} />
    </div>
  )
}
